import type { FC, ReactNode } from 'react'

import { Award, CalendarCheck, Compass, Keyboard } from 'lucide-react'
import { Container } from '@/components/generals/container'
import { SectionHeading } from '@/components/generals/section-heading'

const STEPS = [
    {
        icon: Compass,
        title: "Pick your course",
        text: "Browse the catalog, or tell us your goal and an advisor will suggest the right place to start."
    },
    {
        icon: CalendarCheck,
        title: "Join a small batch",
        text: "Choose weekday mornings, evenings, weekends or live online. Classes never go above 12 students."
    },
    {
        icon: Keyboard,
        title: "Learn by doing",
        text: "Every lesson ends with a hands-on exercise on your own computer, with your instructor right beside you."
    },
    {
        icon: Award,
        title: "Earn your certificate",
        text: "Finish the final project and receive a certificate you can add to your CV and LinkedIn profile."
    }
]

export const HowItWorksSection: FC = (): ReactNode => {
    return (
        <section id='how-it-works' aria-labelledby='how-it-works-title' className='relative overflow-hidden bg-foreground py-20 text-background sm:py-28'>
            <div
                aria-hidden='true'
                className='bg-dots pointer-events-none absolute inset-0 text-background/35 [mask-image:radial-gradient(ellipse_70%_70%_at_50%_100%,black,transparent)]'
            />

            <Container className='relative'>
                <SectionHeading
                    id='how-it-works-title'
                    tone='inverted'
                    eyebrow="How it works"
                    title="Four steps from curious to certified"
                    description="No entrance tests and no long waiting lists. Here is what learning with us looks like, from the first message to the last class."
                />

                <ol className='mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4'>
                    {STEPS.map(({ icon: Icon, title, text }, index) => (
                        <li
                            key={title}
                            className='relative rounded-3xl border border-background/10 bg-background/5 p-6 transition-colors hover:bg-background/10 sm:p-7'
                        >
                            <div className='flex items-center justify-between'>
                                <span className='flex size-11 items-center justify-center rounded-xl bg-accent text-accent-foreground'>
                                    <Icon className='size-5' aria-hidden='true' />
                                </span>

                                <span className='font-mono text-sm text-background/40' aria-hidden='true'>
                                    {String(index + 1).padStart(2, '0')}
                                </span>
                            </div>

                            <h3 className='mt-8 text-xl font-semibold'>
                                {title}
                            </h3>

                            <p className='mt-2 text-sm leading-relaxed text-background/70'>
                                {text}
                            </p>
                        </li>
                    ))}
                </ol>
            </Container>
        </section>
    )
}
